import { Popover, PopoverContent, PopoverTrigger } from '@heroui/react';
import {
  ChevronsUpDown, LifeBuoy, LogOut, Smartphone, User,
} from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import Avatar from 'react-avatar';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { usePermissions } from '@/hooks/usePermissions';
import { useOrganizationState, useUserState } from '@/state';
import { useLocaleNavigate } from '@/utils/locale';

import {
  AvatarBox,
  AvatarChevron,
  CompactUserInfo,
  Divider,
  DropdownContainer,
  HeaderAvatarBox,
  MenuItem,
  MenuItemLabel,
  MenuSection,
  StaffDot,
  UserEmail,
  UserInfoSection,
  UserMenuTrigger,
  UserName,
} from './UserMenu.styled';
import { UserMenuProps } from './UserMenu.types';

export const UserMenu: FC<UserMenuProps> = ({
  userName,
  userEmail,
  userAvatar,
  onProfileClick,
  onLogout,
  showMyProfile = true,
  avatarVariant = 'default',
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isStaff, isOwner: hasOwnerRole } = usePermissions();
  const { owner } = useOrganizationState();
  const { currentUser } = useUserState();
  // Właściciel per organizacja: claim `role: owner` to szybka ścieżka, ale
  // źródłem prawdy jest `organizations/{id}.owner` — to jego pilnują reguły
  // Firestore (`ownsHotel`) i po nim poznajemy właściciela AKTUALNEJ organizacji,
  // a nie kogoś, kto ma rolę ownera gdzie indziej.
  const isOwner = (!!owner && owner === currentUser?.uid) || hasOwnerRole;
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isLight = avatarVariant === 'light';
  // react-avatar liczy inicjały (i pokazuje zdjęcie z `src`, gdy dostępne)
  // z `name`. Gdy brak imienia, używamy emaila jako fallbacku.
  const avatarName = userName?.trim() || userEmail?.trim() || 'U';

  const handleMenuItemClick = (callback?: () => void) => {
    callback?.();
    setIsOpen(false);
  };

  const navigate = useLocaleNavigate();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <Popover
      placement="bottom-start"
      showArrow={false}
      offset={10}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      shadow="none"
      disableAnimation
    >
      <PopoverTrigger>
        <UserMenuTrigger
          aria-label={t('userMenu.userMenu')}
          onClick={() => setIsOpen(!isOpen)}
        >
          <AvatarBox $variant={avatarVariant}>
            <Avatar
              name={avatarName}
              src={userAvatar || undefined}
              size="34"
              round
              maxInitials={2}
              textSizeRatio={2.2}
              color={isLight ? '#ffffff' : '#f3f4f6'}
              fgColor={isLight ? '#000000' : '#374151'}
            />
            {/* Kropka statusu: niebieska dla właściciela organizacji, zielona dla
                pozostałego personelu (claim isStaff). */}
            {isStaff && (
              <StaffDot
                $owner={isOwner}
                aria-label={isOwner
                  ? t('topbar.ownerStatus', 'Właściciel')
                  : t('topbar.staffStatus', 'Personel')}
                title={isOwner
                  ? t('topbar.ownerStatus', 'Właściciel')
                  : t('topbar.staffStatus', 'Personel')}
              />
            )}
          </AvatarBox>
          <AvatarChevron>
            <ChevronsUpDown size={14} strokeWidth={2} />
          </AvatarChevron>
        </UserMenuTrigger>
      </PopoverTrigger>

      <PopoverContent className="p-0 border-none shadow-none">
        <DropdownContainer>
          {/* Kompaktowy header — awatar + imię/email w jednej linii */}
          <UserInfoSection>
            <HeaderAvatarBox>
              <Avatar
                name={avatarName}
                src={userAvatar || undefined}
                size="32"
                round
                maxInitials={2}
                textSizeRatio={2.3}
                color={theme.mode === 'dark' ? '#374151' : '#f3f4f6'}
                fgColor={theme.mode === 'dark' ? '#f3f4f6' : '#374151'}
              />
            </HeaderAvatarBox>
            <CompactUserInfo>
              {userName && <UserName title={userName}>{userName}</UserName>}
              {userEmail && <UserEmail title={userEmail}>{userEmail}</UserEmail>}
            </CompactUserInfo>
          </UserInfoSection>

          <MenuSection>
            {showMyProfile && onProfileClick && (
              <MenuItem onClick={() => handleMenuItemClick(onProfileClick)}>
                <User />
                <MenuItemLabel>{t('userMenu.myProfile', 'Mój profil')}</MenuItemLabel>
              </MenuItem>
            )}

            <MenuItem
              onClick={() => {
                navigate('/support-service');
                setIsOpen(false);
              }}
            >
              <LifeBuoy />
              <MenuItemLabel>{t('userMenu.support', 'Wsparcie')}</MenuItemLabel>
            </MenuItem>
          </MenuSection>

          {onLogout && (
            <>
              <Divider />
              <MenuSection>
                <MenuItem $danger onClick={() => handleMenuItemClick(onLogout)}>
                  <LogOut />
                  <MenuItemLabel>{t('userMenu.logout', 'Wyloguj')}</MenuItemLabel>
                </MenuItem>
              </MenuSection>
            </>
          )}

          <Divider />
          <MenuSection>
            <MenuItem
              onClick={() => {
                navigate('/staff-app');
                setIsOpen(false);
              }}
            >
              <Smartphone />
              <MenuItemLabel>{t('staffApp.sidebar.title', 'Aplikacja dla zespołu')}</MenuItemLabel>
            </MenuItem>
          </MenuSection>
        </DropdownContainer>
      </PopoverContent>
    </Popover>
  );
};
